import { getPreferredLocale } from '@/services/locale.service';

import { buildArtworkImageUrl, deriveArtistName } from './artworkShareMetadata.utils';

const SITE_URL = 'https://suarte.art';
const DEFAULT_IMAGE = 'https://imagedelivery.net/hAeIC__6Aj746x0RFU1joA/b640dc78-9146-4a0e-5d34-1d25ced2ce00/personalEmailSignature';
const NON_INDEXABLE_STATUSES = new Set(['unavailable', 'queued', 'sold_admin_only']);
const SUPPORTED_LOCALES = ['en', 'es', 'fr', 'de', 'it', 'pt'];
const ARTWORK_STATUS_LABELS = {
  original: {
    en: 'Original Artwork',
    es: 'Obra Original',
    fr: 'Œuvre Originale',
    de: 'Originalkunstwerk',
    it: 'Opera Originale',
    pt: 'Obra Original',
  },
  soldOriginal: {
    en: 'Sold Original Artwork',
    es: 'Obra Original Vendida',
    fr: 'Œuvre Originale Vendue',
    de: 'Verkauftes Originalkunstwerk',
    it: 'Opera Originale Venduta',
    pt: 'Obra Original Vendida',
  },
};
const ARTWORK_TITLE_TEMPLATES = {
  en: '{artworkTitle} by {artistName} | {artworkStatusLabel} on Suarte',
  es: '{artworkTitle} de {artistName} | {artworkStatusLabel} en Suarte',
  fr: '{artworkTitle} de {artistName} | {artworkStatusLabel} sur Suarte',
  de: '{artworkTitle} von {artistName} | {artworkStatusLabel} auf Suarte',
  it: '{artworkTitle} di {artistName} | {artworkStatusLabel} su Suarte',
  pt: '{artworkTitle} de {artistName} | {artworkStatusLabel} na Suarte',
};
const ARTWORK_DESCRIPTION_FRAGMENTS = {
  en: {
    discover: 'Discover "{artworkTitle}" by {artistName} on Suarte.',
    themes: 'Themes include {themes}.',
    price: 'Listed at {price}.',
    gallery: 'Presented by {galleryName}.',
    limitedAvailable: 'Limited editions are available.',
    limitedSoldOut: 'Limited editions are sold out.',
  },
  es: {
    discover: 'Descubre "{artworkTitle}" de {artistName} en Suarte.',
    themes: 'Los temas incluyen {themes}.',
    price: 'Precio indicado: {price}.',
    gallery: 'Presentada por {galleryName}.',
    limitedAvailable: 'Hay ediciones limitadas disponibles.',
    limitedSoldOut: 'Las ediciones limitadas están agotadas.',
  },
  fr: {
    discover: 'Découvrez "{artworkTitle}" de {artistName} sur Suarte.',
    themes: 'Les thèmes incluent {themes}.',
    price: 'Prix indiqué : {price}.',
    gallery: 'Présentée par {galleryName}.',
    limitedAvailable: 'Des éditions limitées sont disponibles.',
    limitedSoldOut: 'Les éditions limitées sont épuisées.',
  },
  de: {
    discover: 'Entdecken Sie "{artworkTitle}" von {artistName} auf Suarte.',
    themes: 'Themen sind unter anderem {themes}.',
    price: 'Angegeben zum Preis von {price}.',
    gallery: 'Präsentiert von {galleryName}.',
    limitedAvailable: 'Limitierte Editionen sind verfügbar.',
    limitedSoldOut: 'Limitierte Editionen sind ausverkauft.',
  },
  it: {
    discover: 'Scopri "{artworkTitle}" di {artistName} su Suarte.',
    themes: 'I temi includono {themes}.',
    price: 'Prezzo indicato: {price}.',
    gallery: 'Presentata da {galleryName}.',
    limitedAvailable: 'Sono disponibili edizioni limitate.',
    limitedSoldOut: 'Le edizioni limitate sono esaurite.',
  },
  pt: {
    discover: 'Descubra "{artworkTitle}" de {artistName} na Suarte.',
    themes: 'Os temas incluem {themes}.',
    price: 'Preço indicado: {price}.',
    gallery: 'Apresentada por {galleryName}.',
    limitedAvailable: 'Há edições limitadas disponíveis.',
    limitedSoldOut: 'As edições limitadas estão esgotadas.',
  },
};

function normalizeText(value) {
  if (typeof value !== 'string') {
    return '';
  }

  return value.replace(/\s+/g, ' ').trim();
}

function normalizeLocale(locale) {
  const normalized = normalizeText(locale).toLowerCase();
  const shortCode = normalized.split('-')[0];
  return SUPPORTED_LOCALES.includes(shortCode) ? shortCode : 'en';
}

function truncateText(value, maxLength = 160) {
  const normalized = normalizeText(value);
  if (!normalized || normalized.length <= maxLength) {
    return normalized;
  }

  const shortened = normalized.slice(0, Math.max(0, maxLength - 1)).trim();
  const lastSpace = shortened.lastIndexOf(' ');
  const safeValue = lastSpace > 80 ? shortened.slice(0, lastSpace) : shortened;
  return `${safeValue.trim()}…`;
}

function coerceNumber(value) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function formatNumber(value) {
  const numeric = coerceNumber(value);
  if (numeric === null) {
    return '';
  }

  if (Number.isInteger(numeric)) {
    return String(numeric);
  }

  return numeric.toFixed(2).replace(/\.00$/, '');
}

function formatList(values = [], maxItems = 2) {
  const list = Array.isArray(values)
    ? values.map((value) => normalizeText(value)).filter(Boolean).slice(0, maxItems)
    : [];

  if (!list.length) {
    return '';
  }

  if (list.length === 1) {
    return list[0];
  }

  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
}

function resolveArtworkTitle(artworkData = {}) {
  return normalizeText(artworkData?.artwork_about?.artwork_title || artworkData?.title || artworkData?.artwork_title) || 'Untitled artwork';
}

function resolveArtist(artworkData = {}) {
  return normalizeText(deriveArtistName(artworkData)) || 'Unknown Artist';
}

function resolveGallery(artworkData = {}) {
  return normalizeText(
    artworkData?.artwork_about?.artwork_gallery?.user_name
    || artworkData?.artwork_about?.artwork_gallery?.gallery_name
  );
}

function resolveArtworkDescription(artworkData = {}) {
  const description = truncateText(artworkData?.artwork_about?.artwork_description, 160);
  if (!description) {
    return '';
  }

  const medium = normalizeText(artworkData?.artwork_about?.artwork_medium);
  const dimensions = resolveDimensions(artworkData).label;
  const lead = [medium, dimensions].filter(Boolean).join(', ');
  return truncateText([lead ? `${lead}.` : '', description].filter(Boolean).join(' '), 160);
}

function resolveDimensions(artworkData = {}) {
  const size = artworkData?.artwork_about?.artwork_size ?? {};
  const unit = normalizeText(size?.unit) || 'cm';
  const height = coerceNumber(size?.height);
  const width = coerceNumber(size?.length ?? size?.width);
  const depth = coerceNumber(size?.depth);

  const parts = [formatNumber(height), formatNumber(width)].filter(Boolean);
  return {
    unit,
    height,
    width,
    depth,
    label: parts.length === 2 ? `${parts[0]} x ${parts[1]} ${unit}` : '',
  };
}

function resolvePrice(artworkData = {}) {
  const amount = coerceNumber(artworkData?.artwork_about?.artwork_price);
  const currency = normalizeText(artworkData?.artwork_about?.artwork_currency) || 'EUR';

  return {
    amount,
    currency,
    display: amount === null ? '' : `${currency} ${formatNumber(amount)}`,
  };
}

function resolveImageGallery(artworkData = {}) {
  const primary = buildArtworkImageUrl(artworkData) || DEFAULT_IMAGE;
  const secondary = Array.isArray(artworkData?.artwork_media?.artwork_secondary_pictures)
    ? artworkData.artwork_media.artwork_secondary_pictures
      .map((picture) => buildArtworkImageUrl({ artwork_media: { artwork_main_picture: picture } }))
      .filter(Boolean)
    : [];

  return Array.from(new Set([primary, ...secondary]));
}

function resolveLimitedEditionSummary(artworkData = {}) {
  const inventory = artworkData?.artwork_limited_editions;
  if (!inventory || typeof inventory !== 'object') {
    return { hasLimitedEditions: false, soldOut: false, sizes: [] };
  }

  const sizes = Object.keys(inventory).filter((sizeKey) => {
    const entry = inventory[sizeKey];
    const quantity = coerceNumber(entry?.quantity);
    return quantity !== null && quantity > 0;
  });

  if (!sizes.length) {
    return { hasLimitedEditions: false, soldOut: false, sizes: [] };
  }

  const soldOut = sizes.every((sizeKey) => {
    const entry = inventory[sizeKey];
    const quantity = coerceNumber(entry?.quantity) || 0;
    const sold = coerceNumber(entry?.sold) || 0;
    return quantity <= 0 || sold >= quantity;
  });

  return {
    hasLimitedEditions: true,
    soldOut,
    sizes,
  };
}

function applyTemplate(template, variables = {}, maxLength = 160) {
  const normalizedTemplate = normalizeText(template);
  if (!normalizedTemplate) {
    return '';
  }

  const hydrated = normalizeText(
    normalizedTemplate.replace(/\{([^{}]+)\}/g, (match, key) => {
      if (Object.prototype.hasOwnProperty.call(variables, key)) {
        return normalizeText(String(variables[key] ?? ''));
      }
      return match;
    })
  ).replace(/\s+\./g, '.').replace(/\s+,/g, ',');

  return truncateText(hydrated, maxLength);
}

function isSoldStatus(status = '') {
  return normalizeText(status).toLowerCase().includes('sold');
}

function isPubliclyIndexable(artworkData = {}) {
  const status = normalizeText(artworkData?.artwork_status).toLowerCase();
  if (!artworkData?.slug || artworkData?.artwork_flags?.withdrawn === true) {
    return false;
  }

  return status && !NON_INDEXABLE_STATUSES.has(status);
}

export function buildArtworkSeoMetadata({ artworkData, pathname }) {
  const explicitSeo = artworkData?.__seo;
  if (explicitSeo && typeof explicitSeo === 'object') {
    const duplicateSuarteworldRoute = pathname?.includes('/suarteworld/');
    return {
      ...explicitSeo,
      robots: duplicateSuarteworldRoute ? 'noindex,follow' : explicitSeo.robots,
    };
  }

  const locale = normalizeLocale(artworkData?.__seoLocale || artworkData?.__seo?.locale || getPreferredLocale());

  const title = resolveArtworkTitle(artworkData);
  const artist = resolveArtist(artworkData);
  const gallery = resolveGallery(artworkData);
  const medium = normalizeText(artworkData?.artwork_about?.artwork_medium);
  const dimensions = resolveDimensions(artworkData);
  const price = resolvePrice(artworkData);
  const limitedEditions = resolveLimitedEditionSummary(artworkData);
  const themes = formatList(artworkData?.artwork_about?.artwork_theme, 2);
  const soldOriginal = isSoldStatus(artworkData?.artwork_status);
  const artworkStatusLabel = soldOriginal
    ? (ARTWORK_STATUS_LABELS.soldOriginal[locale] || ARTWORK_STATUS_LABELS.soldOriginal.en)
    : (ARTWORK_STATUS_LABELS.original[locale] || ARTWORK_STATUS_LABELS.original.en);
  const canonicalUrl = `${SITE_URL}/artwork/${artworkData?.slug}`;
  const images = resolveImageGallery(artworkData);
  const artworkDescription = resolveArtworkDescription(artworkData);
  const localizedFragments = ARTWORK_DESCRIPTION_FRAGMENTS[locale] || ARTWORK_DESCRIPTION_FRAGMENTS.en;
  const metaDescription = artworkDescription || truncateText([
    applyTemplate(localizedFragments.discover, { artworkTitle: title, artistName: artist }, 200),
    `${artworkStatusLabel}.`,
    medium || '',
    dimensions.label ? `${dimensions.label}.` : '',
    themes ? applyTemplate(localizedFragments.themes, { themes }, 200) : '',
    price.display ? applyTemplate(localizedFragments.price, { price: price.display }, 200) : '',
    gallery ? applyTemplate(localizedFragments.gallery, { galleryName: gallery }, 200) : '',
    limitedEditions.hasLimitedEditions
      ? limitedEditions.soldOut
        ? localizedFragments.limitedSoldOut
        : localizedFragments.limitedAvailable
      : '',
  ].filter(Boolean).join(' '), 160);

  return {
    indexable: isPubliclyIndexable(artworkData),
    type: 'artwork',
    locale,
    title: applyTemplate(
      ARTWORK_TITLE_TEMPLATES[locale] || ARTWORK_TITLE_TEMPLATES.en,
      { artworkTitle: title, artistName: artist, artworkStatusLabel },
      110
    ),
    metaDescription,
    canonicalUrl,
    robots: pathname?.includes('/suarteworld/')
      ? 'noindex,follow'
      : isPubliclyIndexable(artworkData)
        ? 'index,follow,max-image-preview:large'
        : 'noindex,nofollow',
    ogImage: images[0] || DEFAULT_IMAGE,
    ogType: 'product',
    artworkName: title,
    artistName: artist,
    galleryName: gallery,
    artworkStatus: artworkData?.artwork_status,
    price,
    dimensions,
    imageGallery: images,
    hasLimitedEditions: limitedEditions.hasLimitedEditions,
  };
}

export function buildArtworkStructuredData({ artworkData, metadata }) {
  const dimensions = metadata?.dimensions ?? resolveDimensions(artworkData);
  const imageGallery = metadata?.imageGallery ?? resolveImageGallery(artworkData);

  const entity = {
    '@type': ['Product', 'VisualArtwork'],
    '@id': `${metadata.canonicalUrl}#artwork`,
    name: metadata.artworkName,
    description: metadata.metaDescription,
    image: imageGallery,
    url: metadata.canonicalUrl,
    artMedium: normalizeText(artworkData?.artwork_about?.artwork_medium) || undefined,
    material: normalizeText(artworkData?.artwork_about?.artwork_medium) || undefined,
    creator: {
      '@type': 'Person',
      name: metadata.artistName,
    },
  };

  if (metadata.galleryName) {
    entity.seller = {
      '@type': 'Organization',
      name: metadata.galleryName,
    };
  }

  if (dimensions?.height !== null && dimensions?.height !== undefined) {
    entity.height = {
      '@type': 'QuantitativeValue',
      value: dimensions.height,
      unitCode: dimensions.unit === 'cm' ? 'CMT' : 'INH',
    };
  }

  if (dimensions?.width !== null && dimensions?.width !== undefined) {
    entity.width = {
      '@type': 'QuantitativeValue',
      value: dimensions.width,
      unitCode: dimensions.unit === 'cm' ? 'CMT' : 'INH',
    };
  }

  if (metadata?.price?.amount !== null && metadata?.price?.amount !== undefined) {
    entity.offers = {
      '@type': 'Offer',
      price: metadata.price.amount,
      priceCurrency: metadata.price.currency,
      availability: isSoldStatus(artworkData?.artwork_status)
        ? 'https://schema.org/OutOfStock'
        : 'https://schema.org/InStock',
      itemCondition: 'https://schema.org/NewCondition',
      url: metadata.canonicalUrl,
    };
  }

  if (metadata?.hasLimitedEditions) {
    entity.additionalProperty = [
      {
        '@type': 'PropertyValue',
        name: 'Limited editions',
        value: 'Available on Suarte',
      },
    ];
  }

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${metadata.canonicalUrl}#webpage`,
        url: metadata.canonicalUrl,
        name: metadata.title,
        description: metadata.metaDescription,
        primaryImageOfPage: metadata.ogImage,
        mainEntity: {
          '@id': `${metadata.canonicalUrl}#artwork`,
        },
      },
      entity,
    ],
  };
}
